import { FileText, Loader2, X } from "lucide-react";
import { useDownloadInvoicePdf } from "../service/invoiseService";
import { toast } from "react-toastify";
import { Dialog, DialogContent } from "../utils/components/ui/dialog";

interface InvoiceDownloadDialogProps {
  open: boolean;
  invoiceId: string | number | null;
  onClose: () => void;
}

const InvoiceDownloadDialog = ({
  open,
  invoiceId,
  onClose,
}: InvoiceDownloadDialogProps) => {
  const { mutate: downloadPdf, isPending } = useDownloadInvoicePdf();

  const handleDownload = (isDraft: boolean) => {
    if (!invoiceId) {
      toast.error("No invoice selected.");
      return;
    }
    downloadPdf(
      { invoiceId, isDraft },
      {
        onSuccess: () => {
          toast.success("Invoice opened for printing successfully!");
          onClose();
        },
        onError: (error: Error) => {
          toast.error(`Print failed: ${error.message}`);
        },
      },
    );
  };

  if (!open) return null;

  return (
    <Dialog open={open} onOpenChange={onClose}>
      <DialogContent className="rounded-[20px] border-none bg-white shadow-[0_16px_40px_rgba(26,94,249,0.08)] p-6 w-full max-w-md mx-4 [&>button]:hidden">
        <div className="flex justify-between items-center mb-4">
          <h2 className="text-lg font-semibold text-[#21314A]">
            Print Invoice
          </h2>
          <button
            onClick={onClose}
            disabled={isPending}
            className="text-[#52627C] hover:text-[#21314A] transition-colors"
          >
            <X className="w-5 h-5" />
          </button>
        </div>

        <p className="text-sm text-[#52627C] mb-6">
          Choose the type of invoice you want to print:
        </p>

        <div className="flex flex-col gap-3">
          <button
            type="button"
            onClick={() => handleDownload(false)}
            disabled={isPending}
            className="h-12 w-full rounded-[12px] bg-[#1A5EF9] text-white hover:bg-[#0E4FDE] font-medium text-sm flex items-center justify-center gap-2 transition-colors disabled:opacity-50"
          >
            {isPending ? (
              <Loader2 className="animate-spin w-4 h-4" />
            ) : (
              <FileText className="w-4 h-4" />
            )}
            VAT Invoice
          </button>
          <button
            type="button"
            onClick={() => handleDownload(true)}
            disabled={isPending}
            className="h-12 w-full rounded-[12px] border border-[#1A5EF9] text-[#1A5EF9] hover:bg-[#EEF4FF] font-medium text-sm flex items-center justify-center gap-2 transition-colors disabled:opacity-50"
          >
            {isPending ? (
              <Loader2 className="animate-spin w-4 h-4" />
            ) : (
              <FileText className="w-4 h-4" />
            )}
            Non-VAT Invoice
          </button>
        </div>
      </DialogContent>
    </Dialog>
  );
};

export default InvoiceDownloadDialog;
